import { GearIcon, HomeIcon, LogoutIcon } from '@/components/common/icons';
import { documentsApi } from '@/api/documents.api';
import { signoutArgs } from '@/config/keycloak';
import { readActiveDocumentId, writeActiveDocumentId } from '@/hooks/useActiveDocument';
import { useRoles } from '@/hooks/useRoles';
import { displayNameOfUser } from '@/utils/auth';
import type { FC, MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface LayoutProps {
  children: ReactNode;
}

/** The two header buttons share the prototype's outlined 12 px style; the admin one also has an active look. */
const HEADER_BUTTON =
  'ml-1.5 flex items-center gap-1.5 rounded-[5px] border px-[11px] py-1.5 text-xs font-medium hover:border-primary hover:text-primary';
const HEADER_BUTTON_IDLE = `${HEADER_BUTTON} border-control bg-white text-muted`;
const HEADER_BUTTON_ACTIVE = `${HEADER_BUTTON} border-primary bg-accent-soft text-primary`;

const LANGUAGES = ['cs', 'en'] as const;

/** cs / en in the prototype's small segmented style (its header role switcher). */
const LanguageSwitch: FC = () => {
  const { t, i18n } = useTranslation();
  const current = (i18n.resolvedLanguage ?? i18n.language ?? 'cs').startsWith('en') ? 'en' : 'cs';
  return (
    <div
      role="radiogroup"
      aria-label={t('common.language')}
      className="flex overflow-hidden rounded border border-control bg-white"
    >
      {LANGUAGES.map((language) => (
        <button
          key={language}
          type="button"
          role="radio"
          aria-checked={current === language}
          lang={language}
          onClick={() => {
            i18n.changeLanguage(language);
            document.documentElement.lang = language;
          }}
          className={`cursor-pointer border-none px-[9px] py-[5px] text-[11px] font-semibold uppercase ${
            current === language ? 'bg-primary text-white' : 'bg-white text-muted hover:text-primary'
          }`}
        >
          {language}
        </button>
      ))}
    </div>
  );
};

const Divider: FC = () => <div className="h-7 w-px flex-none bg-control" aria-hidden="true" />;

/** "Petra Nováková" → "PN"; a single word gives its first two letters. */
export const initialsOf = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * The frame around every screen: the prototype's 56 px header, then the screen filling the rest of
 * the viewport. Screens own their scrolling (the review screen scrolls three columns separately).
 *
 * The header carries the user's name only, the "Administrace" button for admins and "Odhlásit".
 */
export const Layout: FC<LayoutProps> = ({ children }) => {
  const auth = useAuth();
  const { isAdmin } = useRoles();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const name = displayNameOfUser(auth.user);

  /**
   * Going home ends the work on the document: it is discarded on the server like "Smazat dokument"
   * does, so nothing is held for the idle TTL. A 404 means it is gone already; any other failure is
   * left to the TTL, since the user is leaving either way.
   */
  const handleHome = async (event: MouseEvent) => {
    const documentId = readActiveDocumentId();
    if (documentId === null) return;
    event.preventDefault();
    writeActiveDocumentId(null);
    navigate('/');
    try {
      await documentsApi.delete(documentId);
    } catch {
      // The backend sweeps it after the idle TTL.
    }
  };
  const isAdminActive = pathname === '/admin' || pathname.startsWith('/admin/');

  // `relative`: without a positioned ancestor, `sr-only` spans deep in a scrolled list stretch the
  // document past `overflow-hidden` and the whole page scrolls.
  return (
    <div className="relative flex h-screen flex-col overflow-hidden bg-surface">
      <a
        href="#main"
        className="sr-only bg-primary px-3 py-2 text-sm font-semibold text-white focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50"
      >
        {t('nav.skipToContent')}
      </a>

      <header className="flex min-h-14 flex-none flex-wrap items-center gap-x-[18px] gap-y-2.5 border-b border-line bg-white px-5 py-1.5">
        <Link to="/" onClick={handleHome} aria-label={t('nav.home')} className="flex flex-none items-center gap-[18px]">
          <img src="/bvk-logo.png" alt={t('app.organisation')} className="block h-[26px] w-auto flex-none" />
          <Divider />
          <div className="flex-none leading-[1.25]">
            <div className="whitespace-nowrap text-[13.5px] font-bold text-primary">{t('app.name')}</div>
            <div className="whitespace-nowrap text-[11px] text-subtle">{t('app.tagline')}</div>
          </div>
        </Link>
        <Link
          to="/"
          onClick={handleHome}
          aria-label={t('nav.home')}
          title={t('nav.home')}
          className={`${HEADER_BUTTON_IDLE} !px-2`}
        >
          <HomeIcon size={14} />
        </Link>
        <div className="flex-1" />
        <Divider />
        <LanguageSwitch />
        {auth.isAuthenticated ? (
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-[27px] w-[27px] items-center justify-center rounded-full bg-accent-soft text-[11px] font-bold text-primary"
              aria-hidden="true"
            >
              {initialsOf(name)}
            </div>
            <div className="text-[12.5px] font-semibold leading-[1.2]">{name}</div>
            {isAdmin ? (
              <Link
                to="/admin"
                aria-current={isAdminActive ? 'page' : undefined}
                className={isAdminActive ? HEADER_BUTTON_ACTIVE : HEADER_BUTTON_IDLE}
                title={t('nav.administration')}
              >
                <GearIcon size={13} />
                <span className="max-[1040px]:sr-only">{t('nav.administration')}</span>
              </Link>
            ) : null}
            {/* `signoutRedirect` alone: it sends the id token hint and clears the user itself.
                Removing the user first would strip the hint and stop Keycloak on its confirmation page. */}
            <button
              type="button"
              className={`${HEADER_BUTTON_IDLE} cursor-pointer`}
              onClick={() => auth.signoutRedirect(signoutArgs())}
            >
              <LogoutIcon size={13} />
              {t('nav.signOut')}
            </button>
          </div>
        ) : null}
      </header>

      <main id="main" className="flex min-h-0 flex-1 flex-col">
        {children}
      </main>
    </div>
  );
};
